// The bulk scan basket: labels collected on a phone before one action is
// applied to all of them.
//
// Nothing is written to stock until the basket is applied, so the basket lives
// on the device. It is kept in storage because each camera-app scan is a fresh
// page load, and it expires after a few idle hours so that a lookup scan the
// next morning gets the ordinary item page rather than a stale basket.
//
// Storage access is wrapped because Safari private mode and some kiosk setups
// throw on localStorage; a basket that cannot be stored simply does not exist.

export const BULK_SCAN_KEY = 'av_bulk_scan';
export const BULK_SCAN_TTL_MS = 4 * 60 * 60 * 1000;

export const STATUS_LABELS = {
  in_stock:       'In Stock',
  deployed:       'Deployed',
  allocated:      'Allocated',
  in_transit:     'In Transit',
  decommissioned: 'Decommissioned',
};

// Accept a raw asset tag or a full scanned label URL (…?scan=AV-INV-00001).
export function extractAssetTag(raw) {
  let t = String(raw || '').trim();
  const m = t.match(/[?&]scan=([^&#]+)/i);
  if (m) { try { t = decodeURIComponent(m[1]); } catch (e) { t = m[1]; } }
  return t.trim().toUpperCase();
}

export function newBasket(now = Date.now()) {
  return { started_at: now, updated_at: now, items: [] };
}

export function readBasket(storage, now = Date.now()) {
  let raw = null;
  try { raw = storage.getItem(BULK_SCAN_KEY); } catch (e) { return null; }
  if (!raw) return null;
  let basket;
  try { basket = JSON.parse(raw); } catch (e) { clearBasket(storage); return null; }
  if (!basket || !Array.isArray(basket.items) || typeof basket.updated_at !== 'number') { clearBasket(storage); return null; }
  if (now - basket.updated_at > BULK_SCAN_TTL_MS) { clearBasket(storage); return null; }
  return basket;
}

export function writeBasket(storage, basket, now = Date.now()) {
  const next = { ...basket, updated_at: now };
  try { storage.setItem(BULK_SCAN_KEY, JSON.stringify(next)); } catch (e) { /* no storage, no basket */ }
  return next;
}

export function clearBasket(storage) {
  try { storage.removeItem(BULK_SCAN_KEY); } catch (e) { /* ignore */ }
}

export function isBulkScanActive(storage, now = Date.now()) {
  return readBasket(storage, now) !== null;
}

// Most recent scan first, so the top of the list is always what was just done.
export function addToBasket(basket, item) {
  const tag = extractAssetTag(item.asset_tag);
  if (basket.items.some(i => i.asset_tag === tag)) return { basket, result: 'duplicate' };
  const entry = {
    asset_tag: tag,
    id: item.id,
    name: item.name || '',
    location_name: item.location_name || '',
    status: item.status || '',
    sow_reference: item.sow_reference || item.allocated_sow_reference || '',
    scanned_at: Date.now(),
  };
  return { basket: { ...basket, items: [entry, ...basket.items] }, result: 'added' };
}

export function removeFromBasket(basket, assetTag) {
  const tag = extractAssetTag(assetTag);
  return { ...basket, items: basket.items.filter(i => i.asset_tag !== tag) };
}

// "Move to Bailey House and set In Stock", for the confirm and result screens.
export function describeAction(action, { locations = [], sows = [] } = {}) {
  const parts = [];
  if (action.location_id !== undefined) {
    const name = action.location_id ? (locations.find(l => l.id === action.location_id)?.name || 'new location') : 'Unknown (no location)';
    parts.push(`Move to ${name}`);
  }
  if (action.status === 'allocated' && action.allocated_to_sow_id) {
    const sow = sows.find(s => s.id === action.allocated_to_sow_id);
    parts.push(`Allocate to ${sow?.reference || 'SOW'}`);
  } else if (action.status) {
    parts.push(`${parts.length ? 'set' : 'Set'} ${STATUS_LABELS[action.status] || action.status}`);
  }
  return parts.join(' and ');
}

// Sound and vibration for each scan outcome, distinguishable without looking:
// a short high beep for added, a low double for a duplicate, a long low buzz
// for an unknown label. Vibration patterns are in milliseconds, alternating
// on and off. iPhones have no navigator.vibrate, so the tones carry the signal
// there.
const FEEDBACK = {
  added:     { tones: [{ freq: 1320, ms: 90 }],                                   vibrate: [40] },
  duplicate: { tones: [{ freq: 660, ms: 80 }, { freq: 660, ms: 80 }],             vibrate: [60, 60, 60] },
  not_found: { tones: [{ freq: 330, ms: 350 }],                                   vibrate: [200, 80, 200] },
};

export function feedbackFor(result) {
  return FEEDBACK[result] || FEEDBACK.not_found;
}

// Schedule a tone pattern on a Web Audio context: one square-wave oscillator
// per note, through a gain with a short ramp at each end so it does not click.
// Returns the time the pattern ends. Generated WAV clips in <audio> elements
// were tried before this and played silently on a real iPhone.
export const TONE_GAP_MS = 60;
const TONE_LEVEL = 0.25;
export function scheduleTones(ctx, tones) {
  let t = ctx.currentTime;
  for (const { freq, ms } of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const end = t + ms / 1000;
    const ramp = Math.min(0.005, ms / 4000);
    osc.type = 'square';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(TONE_LEVEL, t + ramp);
    gain.gain.setValueAtTime(TONE_LEVEL, end - ramp);
    gain.gain.linearRampToValueAtTime(0, end);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(end);
    t = end + TONE_GAP_MS / 1000;
  }
  return t;
}
